import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  Skeleton,
  CalendarIcon,
  Button,
  EditIcon,
  Text,
  DrawerStepper,
} from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import { CardInfo, OrderPaymentPaidAtForm } from '@/components'

import {
  formatLongDate,
  onErrorMessage,
  onSuccessMessage,
  isSaleOrderEditable,
} from '@/helpers'
import { QUERIES_ADMIN, useOrderCenterSaleOrderUpdate } from '@/services'

import styles from './card-order-payment-date.module.css'

import type { CardOrderPaymentDateProps } from './card-order-payment-date.type'
import { OrderPaymentProps, SaleOrderProps } from '@/types'

const CardOrderPaymentDateDefault = ({
  payments,
  saleOrderUuid,
  saleOrderStatus,
}: CardOrderPaymentDateProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentCardsOrderCenterShow')

  const [isOpen, setIsOpen] = useState(false)

  const populateValues = (payments?: OrderPaymentProps[]) => {
    return payments?.map((payment) => ({
      uuid: payment.uuid,
      paidAt: payment.paidAt,
      status: payment.status,
      form: payment.form,
      instalment: payment.instalment,
    })) as OrderPaymentProps[]
  }

  const [values, setValues] = useState<Partial<SaleOrderProps>>({
    payments: populateValues(payments),
  })

  const isEditable = isSaleOrderEditable(saleOrderStatus)

  const queryClient = useQueryClient()

  const { mutateAsync } = useOrderCenterSaleOrderUpdate(saleOrderUuid || '')

  const handleClose = () => {
    setIsOpen(false)
  }

  const onSubmit = async () => {
    const response = await mutateAsync(values)

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrderUuid],
      })
      onSuccessMessage(
        t('orderStatusesSuccessMessageTitle'),
        t('orderStatusesSuccessMessageDescription'),
      )

      setIsOpen(false)
    } else {
      onErrorMessage(
        t('orderStatusesErrorMessageTitle'),
        t('orderStatusesErrorMessageDescription'),
      )
    }
  }

  useEffect(() => {
    if (!isOpen) return setValues({})

    setValues({
      payments: populateValues(payments),
    })
  }, [isOpen])

  return (
    <div>
      <CardInfo
        icon={<CalendarIcon />}
        title={t('orderPaymentDateTitle')}
        className={styles.core}
        actions={
          isEditable && (
            <Button
              variant="ghost"
              className={styles.edit}
              size="small"
              onClick={() => setIsOpen(true)}
            >
              {t('editLabel')}
              <EditIcon />
            </Button>
          )
        }
      >
        {payments?.map((payment) => {
          const showParcelLabel = ['partial'].includes(payment?.form)

          const parcelLabel = `(${payment?.instalment}/${payments?.length})`

          return (
            <div key={payment?.uuid} className={styles['date-info']}>
              <Text variant="text-base">
                {payment?.paidAt
                  ? formatLongDate(payment.paidAt)
                  : t('notInformedLabel')}
              </Text>

              <Text variant="text-small" className={styles.description}>
                ({tEnums(`finance_payment_form_${payment?.form}`)}
                {showParcelLabel && ` ${parcelLabel}`})
              </Text>
            </div>
          )
        })}
      </CardInfo>
      <DrawerStepper
        isOpen={isOpen}
        onHandleOpen={handleClose}
        initialStep="root"
        title={t('orderPaymentDateDrawerTitle')}
        translations={{
          optional: '',
        }}
        steps={{
          root: {
            backNavigate: 'root',
            isOptional: false,
            component: (
              <OrderPaymentPaidAtForm
                onBack={handleClose}
                values={values}
                onChangeValues={(value) => setValues(value)}
                onHandleSubmit={onSubmit}
              />
            ),
          },
        }}
      />
    </div>
  )
}

const CardOrderPaymentDateLoading = () => {
  const { t } = useTranslation('componentCardsOrderCenterShow')
  return (
    <CardInfo
      icon={<CalendarIcon />}
      title={t('orderPaymentDateTitle')}
      className={styles.core}
    >
      <Skeleton width="60%" height="1rem" />
    </CardInfo>
  )
}

export const CardOrderPaymentDate = (props: CardOrderPaymentDateProps) => {
  if (props.isLoading) {
    return <CardOrderPaymentDateLoading />
  }

  return <CardOrderPaymentDateDefault {...props} />
}
